// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { OpportunityFormComponentName } from "../../../core/WorkflowsLayouts";
import { formatWorkflowsDate } from "../../../utils";
import { usMiAddInPersonSecurityClassificationCommitteeReviewV2Opportunity } from "../UsMi/UsMiAddInPersonSecurityClassificationCommitteeReviewV2Opportunity";
import { usMiSecurityClassificationCommitteeReviewV2Opportunity } from "../UsMi/UsMiSecurityClassificationCommitteeReviewV2Opportunity";
import { usMiWardenInPersonSecurityClassificationCommitteeReviewV2Opportunity } from "../UsMi/UsMiWardenInPersonSecurityClassificationCommitteeReviewV2Opportunity";
import { FormBase } from "./FormBase";

type GAPInput = "Good" | "Adequate" | "Poor" | "";
type IISPInput = "1" | "2" | "3" | "4" | "5" | "6" | "N/A" | "";
type STGInput = "1" | "2" | "N/A" | "";

export type UsMiSCCReviewV2DraftData = {
  // Data-populated fields
  prisonerNumber: string;
  prisonerName: string;
  ERD: string;
  AMX: string;
  facility: string;
  lock: string;
  lockDate: string;
  OPT: boolean;
  SMI: boolean;
  STG: STGInput;
  adminSeg: boolean;
  punSeg: boolean;
  tempSeg: boolean;
  adminSegDate: string;
  punSegDate: string;
  tempSegDate: string;
  bondableOffensesWithin6Months: string;
  nonbondableOffensesWithin1Year: string;
  adSegDate1: string;
  adSegDate2: string;
  adSegDate3: string;
  adSegDate4: string;
  adSegReason1: string;
  adSegReason2: string;
  adSegReason3: string;
  adSegReason4: string;
  reportsSinceReview: string;

  // General info fields
  reviewType: string;
  segReason: string;

  // Resident history fields
  segNature: string;
  DD: boolean;
  CMO: boolean;
  ADD: boolean;
  APPROVAL_NA: boolean;
  lastWardenInterview: boolean;
  lastADDInterview: boolean;

  // Team evaluation fields
  stageIISP: IISPInput;
  officerName: string;
  GAP1: GAPInput;
  GAP2: GAPInput;
  pcName: string;

  // SCC fields
  participated: boolean;
  whyNot: string;
  sccStop: boolean;
  comment: string;
  misconductFree: boolean;
  IISP: boolean;
  other1: boolean;
  otherText1: string;
  low: boolean;
  medium: boolean;
  high: boolean;
  potential: string;
  continue: boolean;
  reclassify: boolean;
  transfer: boolean;
  protection: boolean;
  SER: boolean;
  CCBD: boolean;
  UIOA: boolean;
  MRTD: boolean;
  PAP: boolean;
  PRPS: boolean;
  other2: boolean;
  reason: string;
  otherText2: string;
  staffName1: string;
  staffName2: string;
  QMHP: string;

  // Communication method fields
  none: boolean;
  BLV: boolean;
  DHH: boolean;
  other3: boolean;
  ASL: boolean;
  VWHA: boolean;
  VWOHA: boolean;
  slate: boolean;
  staffRead: boolean;
  other4: boolean;
  emergency: boolean;
  security: boolean;
  other5: boolean;
  methodASL: boolean;
  methodVWHA: boolean;
  methodVWOHA: boolean;
  methodSlate: boolean;
  methodStaffRead: boolean;
  other6: boolean;
  VA: boolean;
  responded: boolean;

  // Miscellaneous footer fields
  wardenApproval: boolean;
  wardenInterview: boolean;
  addInterview: boolean;
  residentDist: boolean;
  counselorDist: boolean;
  recordDist: boolean;
  centralDist: boolean;
  addDist: boolean;
};

export const reviewTypeOptions = {
  INITIAL_SCC: "Initial - Within 7 days of Classification",
  MONTHLY_SCC: "At Least Monthly Thereafter",
  PUNITIVE: "Punitive Monthly Warden Approval (Skip to Bottom)",
  WARDEN: "Interview Only - Warden (Skip to Bottom)",
  ADD: "Interview Only - ADD (Skip to Bottom)",
};

export class UsMiSCCReviewV2Form extends FormBase<
  UsMiSCCReviewV2DraftData,
  | usMiSecurityClassificationCommitteeReviewV2Opportunity
  | usMiWardenInPersonSecurityClassificationCommitteeReviewV2Opportunity
  | usMiAddInPersonSecurityClassificationCommitteeReviewV2Opportunity
> {
  get navigateToFormText(): string {
    return "Automate 283 Form";
  }

  get formContents(): OpportunityFormComponentName {
    return "FormUsMiSCCReviewV2";
  }

  get shareFormUpdates(): boolean {
    return true;
  }

  get formType(): string {
    return "UsMiSCCReviewFormV2";
  }

  private formattedReviewType(): string | undefined {
    const { latestSccReviewDate } = this.opportunity.record.metadata;

    switch (this.opportunity.type) {
      case "usMiAddInPersonSecurityClassificationCommitteeReviewV2":
        return reviewTypeOptions.ADD;
      case "usMiWardenInPersonSecurityClassificationCommitteeReviewV2":
        return reviewTypeOptions.WARDEN;
      default:
        // For the base SCC Review opportunity, if no prior review date exists, we
        // can assume this is the initial SCC Review
        return latestSccReviewDate
          ? reviewTypeOptions.MONTHLY_SCC
          : reviewTypeOptions.INITIAL_SCC;
    }
  }

  prefilledDataTransformer(): Partial<UsMiSCCReviewV2DraftData> {
    if (!this.opportunity.record || !this.person) return {};

    const { externalId: prisonerNumber } = this.person;

    const {
      minReleaseDate,
      maxReleaseDate,
      facility,
      lock,
      lockDate,
      OPT,
      SMI,
      segregationType,
      segregationClassificationDate,
      bondableOffensesWithin6Months,
      nonbondableOffensesWithin1Year,
      adSegStaysAndReasonsWithin3Yrs,
      prisonerName,
      reportsSinceReview,
    } = this.opportunity.record.formInformation;

    // Parse the string array representing prior RH stays - each entry has the format:
    // "(startDate,reason1,reason2,etc,)"
    adSegStaysAndReasonsWithin3Yrs?.sort().reverse();
    const adSegStays = adSegStaysAndReasonsWithin3Yrs?.map((segStr) => {
      segStr = segStr.replace("(", "").replace(")", "");
      const splitVals = segStr.split(/,(.*)/, 2);
      return { date: splitVals[0], reasons: splitVals[1] };
    });

    const adminSeg = segregationType === "ADMINISTRATIVE_SOLITARY_CONFINEMENT";
    const punSeg = segregationType === "DISCIPLINARY_SOLITARY_CONFINEMENT";
    const tempSeg = segregationType === "TEMPORARY_SOLITARY_CONFINEMENT";

    const formattedSegDate = segregationClassificationDate
      ? formatWorkflowsDate(segregationClassificationDate)
      : "";

    const formattedLockDate = lockDate ? formatWorkflowsDate(lockDate) : "";

    return {
      prisonerName,
      prisonerNumber,
      ERD: minReleaseDate ? formatWorkflowsDate(minReleaseDate) : "",
      AMX: maxReleaseDate ? formatWorkflowsDate(maxReleaseDate) : "",
      facility,
      lock,
      lockDate: formattedLockDate,
      OPT,
      SMI,
      adminSeg: segregationType === "ADMINISTRATIVE_SOLITARY_CONFINEMENT",
      punSeg: segregationType === "DISCIPLINARY_SOLITARY_CONFINEMENT",
      tempSeg: segregationType === "TEMPORARY_SOLITARY_CONFINEMENT",
      adminSegDate: adminSeg ? formattedSegDate : "",
      punSegDate: punSeg ? formattedSegDate : "",
      tempSegDate: tempSeg ? formattedSegDate : "",
      bondableOffensesWithin6Months: bondableOffensesWithin6Months ?? "",
      nonbondableOffensesWithin1Year: nonbondableOffensesWithin1Year ?? "",
      adSegDate1: adSegStays?.at(0)?.date,
      adSegReason1: adSegStays?.at(0)?.reasons,
      adSegDate2: adSegStays?.at(1)?.date,
      adSegReason2: adSegStays?.at(1)?.reasons,
      adSegDate3: adSegStays?.at(2)?.date,
      adSegReason3: adSegStays?.at(2)?.reasons,
      adSegDate4: adSegStays?.at(3)?.date,
      adSegReason4: adSegStays?.at(3)?.reasons,
      reportsSinceReview,
      reviewType: this.formattedReviewType(),
    };
  }
}
